import { cn } from "@/shared/lib/cn";

/**
 * Connector catalog slugs that ship a full-color brand mark at
 * `/public/ci/<slug>.svg` (colored simple-icons paths, CC0 — generated by
 * `scripts/generate-connector-icons.mjs`). Slugs outside this set — custom
 * servers and brands simple-icons lacks (monday, canva, context7, deepwiki,
 * semgrep, globalping) — fall back to a tonal first-letter tile.
 */
const KNOWN_SLUGS = new Set([
  "todoist",
  "notion",
  "linear",
  "github",
  "sentry",
  "stripe",
  "paypal",
  "intercom",
  "figma",
  "huggingface",
  "vercel",
  "cloudflare",
  "neon",
  "posthog",
  "prisma",
]);

export interface ConnectorIconProps {
  /** Catalog slug ("todoist", …); null/unknown falls back to the name initial. */
  slug?: string | null;
  /** Display name — drives the fallback letter + accessible label. */
  name: string;
  className?: string;
}

/**
 * Slugs whose official brand mark is (near-)black — `theme-light` pins their
 * backing plate to the light token values so the mark stays readable in dark
 * mode instead of vanishing into a dark tile.
 */
const DARK_MARK_SLUGS = new Set(["notion", "vercel", "posthog", "github"]);

/**
 * An MCP connector's brand mark. Known slugs render the full-color brand SVG as
 * a plain `<img>` (the color lives in the asset, so the chrome shows the real
 * brand colors); unknown slugs fall back to a tonal first-letter tile. Size
 * via `className` (e.g. `size-5`). Near-black marks get a light backing plate so
 * they don't disappear on dark surfaces.
 */
export function ConnectorIcon({ slug, name, className }: ConnectorIconProps) {
  const key = (slug ?? "").toLowerCase().trim();
  if (!KNOWN_SLUGS.has(key)) {
    return (
      <span
        role="img"
        aria-label={name}
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-[0.25em] bg-bubble text-[0.55em] font-bold uppercase leading-none text-ink-soft",
          className,
        )}
      >
        {name.trim()[0] ?? "?"}
      </span>
    );
  }

  const img = (
    <img
      src={`/ci/${key}.svg`}
      alt={name}
      className={cn("inline-block shrink-0 object-contain", className)}
    />
  );

  if (DARK_MARK_SLUGS.has(key)) {
    return (
      <span className="theme-light inline-flex shrink-0 items-center justify-center rounded-[0.2em] bg-paper p-[0.12em]">
        {img}
      </span>
    );
  }

  return img;
}
